import { crearTabla, mostrarToast } from '@plataforma/ui';

import {
  aprobarPago,
  obtenerComprobantePago,
  rechazarPago,
  listarPagosBackoffice,
} from '../api/backoffice.js';
import { publicarTienda, actualizarDisponibilidad } from '../api/publicacion.js';
import { exigirSuperAdmin } from '../guardas.js';

import {
  crearBadge,
  crearPaginacion,
  estadoCarga,
  estadoError,
  ESTADOS_PAGO,
  fecha,
  fechaHora,
  moneda,
  motivoRapido,
} from './backoffice-ui.js';
import { montarShell } from './shell.js';

export async function vistaPagos(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/pagos');
  const estadoVista = { estado: '', pagina: 1 };

  contenido.innerHTML = `
    <div class="pf-backoffice-header">
      <div>
        <h1>Facturación / pagos</h1>
        <p class="pf-texto-suave">Gestión de pagos manuales: revisar comprobantes, aprobar o rechazar.</p>
      </div>
    </div>
    <form class="pf-tarjeta pf-backoffice-toolbar" id="pf-filtros-pagos">
      <div class="pf-campo">
        <label for="pf-estado-pagos">Estado</label>
        <select id="pf-estado-pagos">
          <option value="" selected>Todos</option>
          <option value="pendiente">Pendientes</option>
          <option value="aprobado">Aprobados</option>
          <option value="rechazado">Rechazados</option>
          <option value="pagado">Pagados</option>
          <option value="vencido">Vencidos</option>
          <option value="anulado">Anulados</option>
        </select>
      </div>
      <button type="submit" class="pf-boton pf-boton--secundario">Filtrar</button>
    </form>
    <div id="pf-pagos-tabla">${estadoCarga()}</div>
    <pf-modal id="pf-modal-aprobar-pago" titulo="Aprobar pago"></pf-modal>
    <pf-modal id="pf-modal-comprobante-pago" titulo="Comprobante de pago"></pf-modal>
  `;

  contenido.querySelector('#pf-filtros-pagos').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    estadoVista.estado = contenido.querySelector('#pf-estado-pagos').value;
    estadoVista.pagina = 1;
    await cargar(contenido, estadoVista);
  });

  await cargar(contenido, estadoVista);
}

async function cargar(contenido, estadoVista) {
  const destino = contenido.querySelector('#pf-pagos-tabla');
  destino.innerHTML = estadoCarga();

  const { data, error, count } = await listarPagosBackoffice(estadoVista);
  if (error) {
    destino.innerHTML = estadoError('No se pudieron cargar los pagos.');
    return;
  }

  destino.innerHTML = '';
  destino.appendChild(
    crearTabla({
      filaVacia: 'No hay pagos registrados.',
      columnas: [
        { titulo: 'Empresa', render: (f) => f.empresa?.nombre ?? '—' },
        { titulo: 'Plan', render: (f) => f.plan?.nombre ?? '—' },
        { titulo: 'Meses', render: (f) => f.meses_solicitados ?? 1 },
        { titulo: 'Monto', render: (f) => moneda(f.monto) },
        { titulo: 'Estado', render: (f) => crearBadge(ESTADOS_PAGO, f.estado) },
        { titulo: 'Método', render: (f) => f.metodo_pago ?? f.metodo ?? '—' },
        {
          titulo: 'Período',
          render: (f) =>
            f.periodo_inicio ? `${fecha(f.periodo_inicio)} – ${fecha(f.periodo_fin)}` : '—',
        },
        { titulo: 'Enviado', render: (f) => fechaHora(f.creado_en) },
        { titulo: 'Revisado', render: (f) => fechaHora(f.revisado_en) },
        {
          titulo: 'Acciones',
          render: (f) => {
            if (f.estado !== 'pendiente') return '';
            const div = document.createElement('div');
            div.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;';

            const btnAprobar = document.createElement('button');
            btnAprobar.type = 'button';
            btnAprobar.className = 'pf-boton pf-boton--primario pf-boton--chico';
            btnAprobar.textContent = 'Aprobar';
            btnAprobar.addEventListener('click', () =>
              handleAprobar(contenido, f, () => cargar(contenido, estadoVista)),
            );

            const btnRechazar = document.createElement('button');
            btnRechazar.type = 'button';
            btnRechazar.className = 'pf-boton pf-boton--peligro pf-boton--chico';
            btnRechazar.textContent = 'Rechazar';
            btnRechazar.addEventListener('click', () =>
              handleRechazar(f, () => cargar(contenido, estadoVista)),
            );

            div.appendChild(btnAprobar);
            div.appendChild(btnRechazar);
            return div;
          },
        },
        {
          titulo: 'Comprobante',
          render: (f) => {
            if (!f.comprobante_url) return '—';
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pf-boton pf-boton--secundario pf-boton--chico';
            btn.textContent = 'Vista previa';
            btn.addEventListener('click', async (evento) => {
              evento.preventDefault();
              const boton = evento.currentTarget;
              const modal = contenido.querySelector('#pf-modal-comprobante-pago');
              const cuerpo = document.createElement('div');
              cuerpo.className = 'pf-backoffice-comprobante';
              cuerpo.textContent = 'Cargando comprobante…';
              modal.replaceChildren(cuerpo);
              modal.abrir();
              boton.disabled = true;
              const { data, error } = await obtenerComprobantePago(f.id);
              if (error || !data?.url_firmada) {
                mostrarToast('No se pudo obtener el comprobante.', { tono: 'peligro' });
                boton.disabled = false;
                cuerpo.textContent = 'No se pudo cargar la vista previa. Intenta nuevamente.';
                return;
              }

              cuerpo.replaceChildren();
              const esPdf = /\.pdf(?:$|[?#])/i.test(String(f.comprobante_url));
              const vista = document.createElement(esPdf ? 'iframe' : 'img');
              vista.className = esPdf
                ? 'pf-backoffice-comprobante__pdf'
                : 'pf-backoffice-comprobante__imagen';
              if (esPdf) {
                vista.title = 'Vista previa del comprobante PDF';
              } else {
                vista.alt = 'Vista previa del comprobante de pago';
              }
              vista.src = data.url_firmada;
              cuerpo.appendChild(vista);

              const enlace = document.createElement('a');
              enlace.className = 'pf-boton pf-boton--secundario';
              enlace.href = data.url_firmada;
              enlace.target = '_blank';
              enlace.rel = 'noopener noreferrer';
              enlace.textContent = 'Abrir en otra pestaña';
              cuerpo.appendChild(enlace);
              boton.disabled = false;
            });
            return btn;
          },
        },
      ],
      filas: data ?? [],
    }),
  );
  destino.appendChild(
    crearPaginacion({
      total: count ?? 0,
      pagina: estadoVista.pagina,
      alCambiar: async (pagina) => {
        estadoVista.pagina = pagina;
        await cargar(contenido, estadoVista);
      },
    }),
  );
}

function handleAprobar(contenido, pago, alFinalizar) {
  const modal = contenido.querySelector('#pf-modal-aprobar-pago');

  modal.innerHTML = `
    <div style="padding: 2px 0;">
      <p class="pf-texto-suave" style="margin: 0 0 16px;">
        Confirma la aprobación de este pago y revisa los detalles antes de continuar.
      </p>

      <div class="pf-backoffice-detail">
        <div class="pf-backoffice-kv"><span>Empresa</span><strong>${pago.empresa?.nombre ?? '—'}</strong></div>
        <div class="pf-backoffice-kv"><span>Plan</span><strong>${pago.plan?.nombre ?? '—'}</strong></div>
        <div class="pf-backoffice-kv"><span>Periodo</span><strong>${pago.meses_solicitados ?? 1} mes(es)</strong></div>
        <div class="pf-backoffice-kv"><span>Monto</span><strong>${moneda(pago.monto)}</strong></div>
      </div>

      <div class="pf-campo" style="margin-top: 16px;">
        <label for="pf-nota-aprobacion">Nota de aprobación (opcional)</label>
        <textarea
          id="pf-nota-aprobacion"
          rows="3"
          placeholder="Ej. Pago verificado correctamente"
        ></textarea>
      </div>

      <p class="pf-backoffice-note" style="margin-top: 12px; font-size: var(--pf-tamano-sm);">
        Al aprobar el pago, la suscripción será activada o renovada automáticamente y se calculará el periodo correspondiente.
      </p>

      <p class="pf-campo__error" id="pf-error-aprobar-pago" hidden></p>
    </div>

    <div slot="footer" class="pf-formulario__footer" style="margin:0;">
      <button type="button" class="pf-boton pf-boton--secundario" id="pf-cancelar-aprobar">Cancelar</button>
      <button type="button" class="pf-boton pf-boton--primario" id="pf-submit-aprobar">Aprobar pago</button>
    </div>
  `;

  modal.abrir();

  const btnCancelar = modal.querySelector('#pf-cancelar-aprobar');
  const btnAprobar = modal.querySelector('#pf-submit-aprobar');
  const campoError = modal.querySelector('#pf-error-aprobar-pago');

  btnCancelar.addEventListener('click', () => modal.cerrar());

  btnAprobar.addEventListener('click', async () => {
    if (btnAprobar.disabled) return;
    btnAprobar.disabled = true;
    btnAprobar.textContent = 'Procesando...';
    btnCancelar.disabled = true;
    campoError.hidden = true;

    const nota = modal.querySelector('#pf-nota-aprobacion').value.trim();

    const { error } = await aprobarPago(pago.id, nota);

    if (error) {
      campoError.textContent = error.message || 'No se pudo aprobar el pago.';
      campoError.hidden = false;
      btnAprobar.disabled = false;
      btnAprobar.textContent = 'Aprobar pago';
      btnCancelar.disabled = false;
      return;
    }

    modal.cerrar();
    mostrarToast('Pago aprobado correctamente.', { tono: 'exito' });

    // Republicar la tienda y actualizar disponibilidad en segundo plano.
    // Esto asegura que la tienda pública deje de decir "cerrada" y levante
    // cualquier bloqueo 503 HTTP, sin interrumpir al admin si algo falla.
    if (pago.empresa?.id) {
      Promise.allSettled([
        publicarTienda(pago.empresa.id),
        actualizarDisponibilidad(pago.empresa.id),
      ]).then((resultados) => {
        const fallos = resultados.filter(
          (r) => r.status === 'rejected' || (r.status === 'fulfilled' && r.value.error),
        );
        if (fallos.length)
          console.warn('[pagos] Republicación post-aprobación con fallos (best-effort):', fallos);
      });
    }

    await alFinalizar?.();
  });
}

async function handleRechazar(pago, alFinalizar) {
  const motivo = motivoRapido(
    `Rechazar pago de ${moneda(pago.monto)} para ${pago.empresa?.nombre ?? '—'}?\nIngrese el motivo del rechazo (obligatorio):`,
  );
  if (!motivo) {
    if (motivo === '') mostrarToast('El motivo es obligatorio.', { tono: 'alerta' });
    return;
  }

  const { error } = await rechazarPago(pago.id, motivo);
  if (error) {
    mostrarToast(`Error al rechazar: ${error.message}`, { tono: 'peligro' });
    return;
  }
  mostrarToast('Pago rechazado.', { tono: 'alerta' });
  await alFinalizar?.();
}
